// Sprint 8 — Perfil público
import { css } from "styled-system/css";
import { useEffect, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { SocialLinkIcon } from "@/components/profile/social-link-icon";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { renderPublicBioMarkdown } from "@/lib/public-profile-markdown";
import { parsePublicProfileThemeId, publicProfileThemeStyleVars } from "@/lib/public-profile-theme-vars";

type ProfileJson = {
  name: string;
  slug: string;
  bio: string | null;
  category: string | null;
  modality: string;
  acceptsCash?: boolean;
  acceptsTransfer?: boolean;
  acceptsOther?: boolean;
  avatarUrl: string | null;
  primaryColor: string;
  profileTheme: string;
  services: {
    id: string;
    name: string;
    description: string | null;
    duration: number;
    price: number | null;
  }[];
  socialLinks: { label: string; url: string; order: number }[];
};

function modalityLabel(m: string): string {
  if (m === "virtual") return "Virtual";
  if (m === "combinada") return "Combinada";
  return "Presencial";
}

function bannerGradient(primary: string): string {
  const c = primary?.trim() || "#4f46e5";
  return `linear-gradient(135deg, ${c} 0%, color-mix(in srgb, ${c} 68%, white) 100%)`;
}

function formatPriceARS(price: number | null): string {
  if (price == null) return "Consultar";
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(
      price,
    );
  } catch {
    return `$${price}`;
  }
}

export function PublicProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<ProfileJson | null | "missing">(null);

  useEffect(() => {
    if (!slug) return;
    void fetch(`/api/public/profile/${encodeURIComponent(slug)}`).then((r) => {
      if (r.status === 404) {
        setData("missing");
        return;
      }
      void r.json().then(setData);
    });
  }, [slug]);

  if (data === null) {
    return (
      <div
        className={css({
          minH: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: "6",
          bg: "#f4f6f8",
        })}
      >
        <div className={css({ w: "100%", maxW: "520px" })}>
          <LoadingFrame variant="light" minHeight={280} />
        </div>
      </div>
    );
  }

  if (data === "missing" || !slug) {
    return (
      <div className={css({ minH: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: "6" })}>
        <p className={css({ color: "#64748b" })}>Perfil no encontrado.</p>
      </div>
    );
  }

  const themeId = parsePublicProfileThemeId(data.profileTheme);
  const themeVars = publicProfileThemeStyleVars(themeId);
  const primary = data.primaryColor ?? "#4f46e5";

  return (
    <div
      className={css({ minH: "100vh", display: "flex", flexDir: "column" })}
      style={
        {
          ...themeVars,
          backgroundColor: "var(--pp-page-bg)",
          color: "var(--pp-text)",
        } as CSSProperties
      }
    >
      <header
        className={css({ pt: "0", pb: "10", px: "4" })}
        style={{
          background: bannerGradient(primary),
          color: "#ffffff",
        }}
      >
        <div className={css({ maxW: "40rem", mx: "auto", textAlign: "center", pt: "10" })}>
          <div
            className={css({
              w: "96px",
              h: "96px",
              mx: "auto",
              rounded: "full",
              overflow: "hidden",
              borderWidth: "4px",
              borderColor: "rgba(255,255,255,0.85)",
              bg: "rgba(255,255,255,0.2)",
            })}
          >
            {data.avatarUrl ? (
              <img src={data.avatarUrl} alt="" className={css({ w: "100%", h: "100%", objectFit: "cover" })} />
            ) : (
              <div
                className={css({
                  w: "100%",
                  h: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2xl",
                  fontWeight: "bold",
                  color: "white",
                })}
              >
                {data.name.slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>
          <h1 className={css({ fontSize: "2xl", fontWeight: "bold", mt: "4", textShadow: "0 1px 2px rgba(0,0,0,0.15)" })}>
            {data.name}
          </h1>
          {data.category ? (
            <p className={css({ fontSize: "sm", opacity: 0.95, mt: "1" })}>{data.category}</p>
          ) : null}
          <p className={css({ fontSize: "xs", opacity: 0.9, mt: "1" })}>{modalityLabel(data.modality)}</p>
          <div className={css({ display: "flex", flexWrap: "wrap", gap: "2", justifyContent: "center", mt: "3" })}>
            {data.acceptsCash !== false ? (
              <span
                className={css({
                  fontSize: "xs",
                  fontWeight: "semibold",
                  px: "3",
                  py: "1",
                  rounded: "full",
                  bg: "rgba(255,255,255,0.22)",
                  borderWidth: "1px",
                  borderColor: "rgba(255,255,255,0.45)",
                })}
              >
                Efectivo
              </span>
            ) : null}
            {data.acceptsTransfer !== false ? (
              <span
                className={css({
                  fontSize: "xs",
                  fontWeight: "semibold",
                  px: "3",
                  py: "1",
                  rounded: "full",
                  bg: "rgba(255,255,255,0.22)",
                  borderWidth: "1px",
                  borderColor: "rgba(255,255,255,0.45)",
                })}
              >
                Transferencia
              </span>
            ) : null}
            {data.acceptsOther !== false ? (
              <span
                className={css({
                  fontSize: "xs",
                  fontWeight: "semibold",
                  px: "3",
                  py: "1",
                  rounded: "full",
                  bg: "rgba(255,255,255,0.22)",
                  borderWidth: "1px",
                  borderColor: "rgba(255,255,255,0.45)",
                })}
              >
                Otros medios
              </span>
            ) : null}
          </div>
        </div>
      </header>

      <main className={css({ flex: "1", px: "4", pb: "10", maxW: "40rem", w: "100%", mx: "auto", mt: "-6" })}>
        <div
          className={css({
            rounded: "2xl",
            p: "5",
            borderWidth: "1px",
            borderColor: "var(--pp-border)",
            boxShadow: "0 10px 40px rgba(15,23,42,0.06)",
          })}
          style={{ backgroundColor: "var(--pp-card-bg)" }}
        >
          {data.bio?.trim() ? (
            <div className={css({ fontSize: "sm", color: "var(--pp-text)" })}>{renderPublicBioMarkdown(data.bio)}</div>
          ) : (
            <p className={css({ fontSize: "sm", color: "var(--pp-muted)" })}>Sin descripción por ahora.</p>
          )}

          <div className={css({ mt: "6", display: "flex", flexWrap: "wrap", gap: "3", justifyContent: "center" })}>
            {data.socialLinks.map((l, i) => (
              <a
                key={`${l.url}-${i}`}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className={css({
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2",
                  fontSize: "sm",
                  fontWeight: "medium",
                  color: "var(--pp-text)",
                  textDecoration: "none",
                  px: "3",
                  py: "2",
                  rounded: "lg",
                  borderWidth: "1px",
                  borderColor: "var(--pp-border)",
                  _hover: { opacity: 0.9 },
                })}
                style={{ backgroundColor: "var(--pp-page-bg)" }}
              >
                <SocialLinkIcon url={l.url} size={18} />
                <span>{l.label}</span>
              </a>
            ))}
          </div>

          <div className={css({ mt: "8" })}>
            <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "var(--pp-muted)", mb: "3", textTransform: "uppercase", letterSpacing: "0.04em" })}>
              Servicios
            </p>
            <ul className={css({ display: "flex", flexDir: "column", gap: "3", listStyle: "none", p: "0", m: "0" })}>
              {data.services.map((s) => (
                <li
                  key={s.id}
                  className={css({
                    p: "4",
                    rounded: "xl",
                    borderWidth: "1px",
                    borderColor: "var(--pp-border)",
                  })}
                  style={{ backgroundColor: "var(--pp-page-bg)" }}
                >
                  <div className={css({ display: "flex", justifyContent: "space-between", gap: "3", alignItems: "baseline" })}>
                    <span className={css({ fontWeight: "semibold" })}>{s.name}</span>
                    <span className={css({ fontSize: "sm", color: "var(--pp-muted)", whiteSpace: "nowrap" })}>
                      {formatPriceARS(s.price)}
                    </span>
                  </div>
                  {s.description?.trim() ? (
                    <p className={css({ fontSize: "sm", color: "var(--pp-muted)", mt: "2", lineHeight: "1.5" })}>
                      {s.description}
                    </p>
                  ) : null}
                  <p className={css({ fontSize: "xs", color: "var(--pp-muted)", mt: "2" })}>Duración: {s.duration} min</p>
                </li>
              ))}
            </ul>
          </div>

          <div className={css({ mt: "8", textAlign: "center" })}>
            <Link
              to={`/book/${encodeURIComponent(slug)}`}
              className={css({
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                px: "6",
                py: "3",
                rounded: "xl",
                fontSize: "sm",
                fontWeight: "semibold",
                color: "white",
                textDecoration: "none",
                width: "100%",
                maxW: "100%",
              })}
              style={{ background: primary }}
            >
              Reservar turno
            </Link>
          </div>
        </div>
      </main>

      <footer className={css({ py: "6", textAlign: "center", fontSize: "xs", color: "var(--pp-muted)" })}>
        Powered by SFTURNOS
      </footer>
    </div>
  );
}
